import { type FormEvent, useState } from "react"
import { Link } from "react-router-dom"
import { ListView } from "../components/board"
import { Plus } from "../components/icons"
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  Spinner,
  TextArea,
  formatDate,
  useLoad,
  useToast,
} from "../components/ui"
import { type Project, type Task, type Usage, api } from "../lib/api"
import { COLOR_DOT, DEFAULT_CONFIG } from "../lib/boardConfig"
import { type RosterPerson, rosterFrom } from "../lib/roster"
import { useSession } from "../lib/auth"

// Dashboard, the project list, and "My tasks".
//
// Tasks are still not created here. The agent has the developer's checkout in
// front of it and grounds every subtask in files it actually opened; a form in
// a browser can only produce a title and a guess. Projects *are* created here,
// because a project is a name and a short key — both of which a person
// knows before any code has been read.

/** Member id → name, for avatars and assignee menus. Every board page needs
 *  it, and the endpoint is small enough to fetch per page rather than cache.
 *  Readable by every role — the admin-only People list is not a roster. */
export function useRoster() {
  const people = useLoad(() =>
    api.get<{ employees: RosterPerson[] }>("/auth/employees/roster?include=inactive"),
  )
  return rosterFrom(people.data?.employees ?? [])
}

export function DashboardPage() {
  const { company, employee } = useSession()
  const projects = useLoad(() => api.get<{ projects: Project[] }>("/projects"))
  // The external role never sees cost, and its /analytics/overview is refused
  // at the API — so do not call it, and drop the two stats below.
  const isExternal = employee.role === "external"
  const usage = useLoad(() =>
    isExternal ? Promise.resolve(null) : api.get<{ usage: Usage }>("/analytics/overview"),
  )
  const mine = useLoad(() => api.get<{ tasks: Task[] }>("/projects/tasks?assigneeId=me"))

  const open = (mine.data?.tasks ?? []).filter((t) => t.status !== "done")

  return (
    <>
      <PageHeader
        title={`Hello, ${employee.name.split(" ")[0]}`}
        subtitle={isExternal ? company.name : `${company.name} · ${company.creditsFormatted} in credits`}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        {!isExternal && (
          <>
            <Stat label="Credits" value={company.creditsFormatted} />
            <Stat
              label="Spent so far"
              value={usage.data?.usage.chargedFormatted ?? "—"}
              hint={usage.data ? `${usage.data.usage.sessions ?? 0} sessions` : undefined}
            />
          </>
        )}
        <Stat label="Assigned to you" value={String(open.length)} hint="Open tasks" />
      </div>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Your tasks</h2>
          <Link to="/my-tasks" className="text-sm text-interactive hover:underline">
            View all
          </Link>
        </div>
        {mine.loading ? (
          <Skeleton className="h-24" />
        ) : open.length ? (
          <ListView tasks={open.slice(0, 8)} showProject />
        ) : (
          <Empty
            title="Nothing assigned to you"
            hint="Tasks land here once someone puts your name on one."
          />
        )}
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Projects</h2>
          <Link to="/projects" className="text-sm text-interactive hover:underline">
            View all
          </Link>
        </div>
        {projects.loading ? (
          <Skeleton className="h-20" />
        ) : projects.data?.projects.length ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {projects.data.projects.slice(0, 4).map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        ) : (
          <Empty
            title="No projects yet"
            hint="Create one here, then run the agent in the folder holding your code and ask it to plan the work."
            action={
              <Link to="/projects">
                <Button variant="ghost">Go to projects</Button>
              </Link>
            }
          />
        )}
      </section>
    </>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className="text-xs text-muted">{label}</span>
      <span className="font-mono text-xl font-semibold tabular-nums text-foreground">{value}</span>
      {hint && <span className="text-xs text-faint">{hint}</span>}
    </Card>
  )
}

// The bar under a project name: one segment per status, width by share. It
// answers "how far along is this" without the reader doing arithmetic on five
// numbers.
function ProjectCard({ project }: { project: Project }) {
  const counts = project.counts
  // Each card colours by its own board's config — two projects can name and
  // paint their columns differently and both bars stay truthful.
  const statuses = project.config?.statuses ?? DEFAULT_CONFIG.statuses
  const total = statuses.reduce((sum, s) => sum + (counts[s.id] ?? 0), 0)

  return (
    <Link to={`/projects/${project.id}`}>
      <Card className="flex h-full flex-col gap-3 p-4 transition-colors hover:border-border-strong">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{project.name}</p>
            <p className="truncate text-sm text-muted">{project.description || "No description"}</p>
          </div>
          <span className="shrink-0 rounded border border-border px-1.5 py-0.5 font-mono text-[11px] text-faint">
            {project.keyPrefix ?? "—"}
          </span>
        </div>

        {total > 0 ? (
          <>
            <div className="flex h-1.5 overflow-hidden rounded-full bg-surface-muted">
              {statuses.map((status) =>
                counts[status.id] ? (
                  <span
                    key={status.id}
                    className={COLOR_DOT[status.color] ?? COLOR_DOT.gray}
                    style={{ width: `${((counts[status.id] ?? 0) / total) * 100}%` }}
                    title={`${counts[status.id]} ${status.name.toLowerCase()}`}
                  />
                ) : null,
              )}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-faint">
              {statuses.map((status) =>
                counts[status.id] ? (
                  <span key={status.id}>
                    <span className="font-mono tabular-nums text-muted">{counts[status.id]}</span>{" "}
                    {status.name.toLowerCase()}
                  </span>
                ) : null,
              )}
            </div>
          </>
        ) : (
          <p className="text-xs text-faint">No tasks yet</p>
        )}
      </Card>
    </Link>
  )
}

export function ProjectsPage() {
  const [showArchived, setShowArchived] = useState(false)
  const [creating, setCreating] = useState(false)
  const { data, loading, error, reload } = useLoad(() =>
    api.get<{ projects: Project[] }>("/projects?includeArchived=true"),
  )

  const all = data?.projects ?? []
  const active = all.filter((p) => !p.archivedAt)
  const archived = all.filter((p) => p.archivedAt)

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="One project per system being modernised."
        action={
          <Button onClick={() => setCreating(true)}>
            <Plus /> New project
          </Button>
        }
      />

      <ErrorNote error={error} />

      {loading ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      ) : active.length ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {active.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      ) : (
        <Empty
          title="No projects yet"
          hint="Create one, then run the agent in the folder holding your code and ask it to plan the work — it reads the repository and writes tasks as it goes."
          action={<Button onClick={() => setCreating(true)}>New project</Button>}
        />
      )}

      {archived.length > 0 && (
        <section className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="self-start text-xs text-muted underline underline-offset-2 hover:text-foreground"
          >
            {showArchived ? "Hide" : "Show"} {archived.length} archived
          </button>
          {showArchived && (
            <div className="flex flex-col gap-2">
              {archived.map((p) => (
                <ArchivedRow key={p.id} project={p} onChange={reload} />
              ))}
            </div>
          )}
        </section>
      )}

      {creating && <NewProjectModal onClose={() => setCreating(false)} onCreated={reload} />}
    </>
  )
}

function ArchivedRow({ project, onChange }: { project: Project; onChange: () => void }) {
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  return (
    <Card className="flex items-center justify-between gap-4 p-3">
      <div className="min-w-0">
        <p className="truncate text-sm text-muted">{project.name}</p>
        <p className="text-xs text-faint">
          Archived {project.archivedAt ? formatDate(project.archivedAt) : ""}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          try {
            await api.post(`/projects/${project.id}/restore`, {})
            toast(`${project.name} restored`)
            onChange()
          } finally {
            setBusy(false)
          }
        }}
      >
        Restore
      </Button>
    </Card>
  )
}

function NewProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const toast = useToast()

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const keyPrefix = String(form.get("keyPrefix") ?? "").trim().toUpperCase()

    setBusy(true)
    setError(null)
    try {
      await api.post("/projects", {
        name: String(form.get("name") ?? "").trim(),
        description: String(form.get("description") ?? "").trim() || undefined,
        // Left out entirely when blank, so the server derives one from the name
        // rather than being handed an empty string to validate.
        ...(keyPrefix ? { keyPrefix } : {}),
      })
      toast("Project created")
      onCreated()
      onClose()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="New project" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Name" name="name" required autoFocus placeholder="Checkout modernisation" />
        <TextArea label="Description" name="description" placeholder="What this covers, and why." />
        <Field
          label="Key prefix"
          name="keyPrefix"
          maxLength={10}
          pattern="[A-Za-z][A-Za-z0-9_]{1,9}"
          placeholder="CHK"
          hint="2 to 10 letters, digits or _, starting with a letter, used for task keys like CHK-42. Left blank, three letters are derived from the name."
        />
        <ErrorNote error={error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create project"}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export function MyTasksPage() {
  const { data, loading, error } = useLoad(() =>
    api.get<{ tasks: Task[] }>("/projects/tasks?assigneeId=me"),
  )
  const tasks = data?.tasks ?? []

  return (
    <>
      <PageHeader title="My tasks" subtitle="Everything assigned to you, across every project." />
      <ErrorNote error={error} />
      {loading ? (
        <Spinner />
      ) : tasks.length ? (
        <ListView tasks={tasks} showProject />
      ) : (
        <Empty
          title="Nothing assigned to you"
          hint="Open a project and put your name on a task, or ask a colleague to."
        />
      )}
    </>
  )
}
